import { unstable_cache } from "next/cache";
import type { SupportedGames } from "@/types/game";

/**
 * Bulk game data from tcgfan-backend, which reads the shared `product_data`
 * table rather than the json files these payloads used to ship as.
 *
 *   /api/cards?game=<game>          every card, keyed by card id
 *   /api/data/aa-data?game=<game>   printings per card number, with tcgplayer_id
 *   /api/data/groups?game=<game>    TCGplayer groups, one per set
 *
 * Games whose cards are bundled as static json (One Piece, Union Arena,
 * Riftbound, Naruto) do not come through here. Palworld does: its cards live
 * only in the database, so there is no json file to ship and no R2 copy to keep
 * in step with a re-scrape.
 *
 * Server-side only -- these calls carry BACKEND_SECRET_KEY.
 */

/** One card's fields, exactly as stored in product_data's `data` blob. */
export type RawCard = Record<string, unknown>;

/**
 * One printing of a card, from `/api/data/aa-data`. The frontend only needs the
 * TCGplayer mapping off it, but the shape matches the aa_data.json the bundled
 * games still import.
 */
export interface RawAaCard {
  id: string;
  name: string | null;
  card_number: string | null;
  set: string | null;
  src: string | null;
  tcgplayer_id: string | null;
  asia_en_id: string | null;
  card_sets: string[] | null;
  tags: string[] | null;
  hide: boolean | null;
  count: number | null;
}

function getApiUrl(): string {
  return (
    process.env.BACKEND_API_URL ||
    process.env.NEXT_PUBLIC_API_URL ||
    "https://api.tcgfan.com"
  );
}

/**
 * One authenticated GET against the backend, returning undefined rather than
 * throwing: a missing payload should degrade the page (no prices, no cards),
 * not 500 it.
 *
 * `label` names the call in logs; `path` is everything after the host.
 */
async function fetchFromBackend<T>(
  label: string,
  path: string,
): Promise<T | undefined> {
  const secretKey = process.env.BACKEND_SECRET_KEY;

  if (!secretKey) {
    console.error(
      `[cards-db] BACKEND_SECRET_KEY is not configured, so ${label} cannot ` +
        "be fetched. Set it in the environment.",
    );
    return undefined;
  }

  const apiUrl = getApiUrl();
  const separator = path.includes("?") ? "&" : "?";
  const url = `${apiUrl}${path}${separator}API_KEY=${secretKey}`;

  try {
    const response = await fetch(url, { next: { revalidate: 3600 } });

    if (!response.ok) {
      const body = await response.text().catch(() => "unreadable body");
      console.error(
        `[cards-db] ${label}: ${response.status} ${response.statusText} from ` +
          `${apiUrl}${path} (key hidden). ${body.substring(0, 200)}`,
      );
      return undefined;
    }

    const data = await response.json();

    // Maps (cards, aa data) and lists (groups) are both valid payloads; a
    // string or a number is not.
    if (!data || typeof data !== "object") {
      console.error(
        `[cards-db] ${label}: expected an object, got ${typeof data}`,
      );
      return undefined;
    }

    return data as T;
  } catch (error) {
    console.error(`[cards-db] ${label}: request failed`, error);
    return undefined;
  }
}

/** Every card for a game, keyed by card id. Cached for an hour. */
export const getCardsFromAPI = (game: SupportedGames) =>
  unstable_cache(
    () =>
      fetchFromBackend<Record<string, RawCard>>(
        `${game} cards`,
        `/api/cards?game=${game}`,
      ),
    ["cards-db", game],
    { revalidate: 3600, tags: [`cards-${game}`] },
  )();

/**
 * Every printing for a game, keyed by card number. This is the source of the
 * card -> TCGplayer product id mapping the bundled games read out of
 * aa_data.json. Cached for an hour.
 */
export const getAaDataFromAPI = (game: SupportedGames) =>
  unstable_cache(
    () =>
      fetchFromBackend<Record<string, RawAaCard[]>>(
        `${game} aa data`,
        `/api/data/aa-data?game=${game}`,
      ),
    ["aa-data-db", game],
    { revalidate: 3600, tags: [`aa-data-${game}`] },
  )();

/**
 * One TCGplayer group -- a set as the price data spells it. `groupId` is the
 * `g` carried on a price row, which is what the market watch filter matches on.
 */
export interface RawGroup {
  groupId: string;
  set: string;
  setName: string;
  count: number;
}

/**
 * A game's TCGplayer groups, largest first. Cached for an hour.
 *
 * The market watch set filter used to be a hardcoded list of One Piece group
 * ids served to every game, so every other game offered One Piece sets to
 * filter by.
 */
export const getGroupsFromAPI = (game: SupportedGames) =>
  unstable_cache(
    () =>
      fetchFromBackend<RawGroup[]>(
        `${game} groups`,
        `/api/data/groups?game=${game}`,
      ),
    ["groups-db", game],
    { revalidate: 3600, tags: [`groups-${game}`] },
  )();
